import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { encodeData } from '@/utils';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthType } = useAuthStore();
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const handleCallback = async () => {
      try {
        // Wait for Supabase to process the OAuth hash
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (!session?.user) {
          // No session — try listening for the auth state change (OAuth hash processing)
          const { data } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (!mounted) return;
            if (event === 'SIGNED_IN' && sess?.user) {
              data.subscription.unsubscribe();
              await resolveRedirect(sess.user);
            }
          });

          // Timeout fallback — if no auth event fires
          setTimeout(() => {
            if (mounted) {
              setAuthType('signin');
              navigate('/signin');
            }
          }, 5000);
          return;
        }

        await resolveRedirect(session.user);
      } catch (err) {
        console.error('Auth callback error:', err);
        if (mounted) {
          setError(err.message);
          setTimeout(() => navigate('/signin'), 3000);
        }
      }
    };

    const resolveRedirect = async (user) => {
      if (!mounted) return;

      try {

        const existingUser = await apiClient.getUserByUserId(user.id);
        if (existingUser?.org_id) {
          
          if(existingUser?.role=="broker" || existingUser?.role=="operator")
          {
            const org = await apiClient.getOrganizationById(existingUser?.org_id);
            const branding = org?.branding;
            if (!branding?.color || !branding?.logo || !branding?.contract) {
              navigate(`/onboarding?d=${encodeURIComponent(encodeData({ userTypes: [existingUser?.role] }))}`);
            }
          }

          await useAuthStore.getState().fetchUserProfile();

          const userProfile = useAuthStore.getState().userProfile;

          const status = userProfile?.status;
          if (status !== 'ACTIVE') {
            const messages = {
              ONBOARDING_STARTED: "Your account creation is under review and will be completed soon. This review process usually takes up to 2 working days, but we'll notify you as soon as it's finished.",
              INACTIVE: "Your account has been deactivated. Contact support.",
              SUSPENDED: "Your account has been suspended. Contact support.",
              ARCHIVED: "Your account has been archived. Contact support to reactivate.",
            };
            toast.warning(messages[status] || "Your account is not active. Contact support.");
            await supabase.auth.signOut();
            navigate('/');
            setTimeout(() => setAuthType('signin'), 100);
            return;
          }

          // if account is approved redirect to welcome page
          if(!userProfile?.is_active)
          {
             navigate('/welcome');
             return;
          }

          // Fully registered user — go to dashboard
          navigate('/dashboard');

        } else {
          if(existingUser?.role=="broker" || existingUser?.role=="operator")
          {
            // User exists but no org — needs onboarding
            navigate(`/onboarding?d=${encodeURIComponent(encodeData({ userTypes: [existingUser?.role] }))}`);
          }
          navigate("/pofile-setup");
        }
      } catch (error) {
        console.error('AuthCallback error:', error);
        navigate(`/onboarding?d=${encodeURIComponent(encodeData({ progress: 'pending' }))}`);
      }
    };

    handleCallback();

    return () => {
      mounted = false;
    };
  }, [navigate, setAuthType]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <p className="text-red-500 text-sm">{error}</p>
        ) : (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
